import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { BookOpen, Chrome, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { signInTeacherWithGoogle, ensureTeacherProfile } from '../../utils/supabase/auth';
import { supabase, getCurrentUser } from '../../utils/supabase/client';
import { getSessionWithRetry, handleOAuthCallback } from '../../utils/supabase/oauth-handler';

export default function TeacherLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if already authenticated
  useEffect(() => {
    checkExistingAuth();
  }, []);

  const checkExistingAuth = async () => {
    try {
      console.log('Checking for existing auth session...');
      
      // First check if we have OAuth callback parameters in URL
      if (window.location.hash.includes('access_token')) {
        console.log('OAuth callback detected in URL, processing...');
        const oauthResult = await handleOAuthCallback();
        if (oauthResult.success) {
          console.log('OAuth callback processed successfully');
        } else {
          console.warn('OAuth callback warning:', oauthResult.message);
        }
        // Ensure hash is cleaned even if handleOAuthCallback didn't clean it
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
      
      // Try to get session with retry (handles async OAuth setup)
      const session = await getSessionWithRetry(3, 100);
      if (session?.user) {
        console.log('Existing session found, redirecting to dashboard');
        navigate('/teacher/dashboard');
      } else {
        console.log('No existing session found');
      }
    } catch (error) {
      console.error('Error checking auth:', error);
    } finally {
      setCheckingAuth(false);
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    let isMounted = true;
    const unsubscribers: Array<() => void> = [];

    const setupAuthListener = async () => {
      try {
        // Subscribe to auth state changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!isMounted) return;
          
          console.log('Auth state change event:', event);
          
          if (event === 'SIGNED_IN' && session?.user) {
            try {
              console.log('User signed in:', session.user.id);
              
              // Create or update teacher profile
              await ensureTeacherProfile(session.user.id, {
                full_name: session.user.user_metadata?.full_name || 
                           session.user.user_metadata?.name || 
                           session.user.email?.split('@')[0] || 
                           'Teacher',
                avatar_url: session.user.user_metadata?.avatar_url || 
                           session.user.user_metadata?.picture,
                email: session.user.email
              });

              toast.success('Welcome! Redirecting to dashboard...');
              
              // Small delay to ensure session is persisted
              setTimeout(() => {
                if (isMounted) {
                  // Use replace to avoid back-button loop, and navigate
                  // to a clean path without the OAuth hash fragment
                  navigate('/teacher/dashboard', { replace: true });
                }
              }, 500);
            } catch (error) {
              console.error('Error creating profile:', error);
              toast.error('Error setting up your profile. Please try again.');
            }
          } else if (event === 'SIGNED_OUT') {
            console.log('User signed out');
            if (isMounted) {
              navigate('/teacher/login');
            }
          } else if (event === 'USER_UPDATED') {
            console.log('User updated');
          }
        });

        if (subscription) {
          unsubscribers.push(() => subscription.unsubscribe());
        }
      } catch (error) {
        console.error('Error setting up auth listener:', error);
      }
    };

    setupAuthListener();

    return () => {
      isMounted = false;
      unsubscribers.forEach(unsub => unsub());
    };
  }, [navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await signInTeacherWithGoogle();
      // User will be redirected to Google, then back to the app
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Failed to sign in. Please try again.');
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="fixed inset-0 bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#f8f9fa] flex flex-col">
      {/* Top Bar */}
      <header className="flex items-center justify-between px-4 lg:px-6 h-14 lg:h-16 flex-shrink-0 border-b border-[#e0e0e0] bg-white">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
        >
          <img
            src="/edulive-logo.png"
            alt="EduChemMix"
            className="w-8 h-8 lg:w-9 lg:h-9 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg bg-primary items-center justify-center hidden">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="text-base lg:text-lg font-semibold text-[#202124]">
            EduChemMix <span className="text-muted-foreground font-normal text-sm">JHS</span>
          </span>
        </button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Back to Home
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-sm px-8 py-10 lg:px-10 lg:py-12">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <h1 className="text-2xl font-normal text-[#202124] mb-1">
                Teacher Sign In
              </h1>
              <p className="text-sm text-[#5f6368]">
                Sign in to create and manage your interactive lessons
              </p>
            </div>

            {/* Google Sign In Button */}
            <Button
              onClick={handleGoogleLogin}
              disabled={loading}
              variant="outline"
              className="w-full h-12 rounded-full text-sm font-medium border border-[#dadce0] bg-white hover:bg-[#f8f9fa] text-[#3c4043] shadow-sm transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Sign in with Google
                </>
              )}
            </Button>

            <p className="text-xs text-center text-[#5f6368] mt-4">
              Secured with Google authentication
            </p>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#e0e0e0]"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-4 bg-white text-[#5f6368]">Not a teacher?</span>
              </div>
            </div>

            {/* Student Join Link */}
            <Button
              variant="ghost"
              onClick={() => navigate('/join')}
              className="w-full h-11 rounded-full text-sm font-medium text-primary hover:bg-primary/5"
            >
              Join a session as a student
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-center text-xs text-[#5f6368] mt-5">
            Need help?{' '}
            <button
              onClick={() => navigate('/')}
              className="text-primary hover:underline font-medium"
            >
              Contact your school administrator
            </button>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="flex items-center justify-center px-4 h-12 flex-shrink-0 border-t border-[#e0e0e0] bg-white">
        <p className="text-xs text-[#5f6368]">
          EduChemMix-JHS &copy; 2026. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
